import { getToken } from '../../utils/token';
import { extractIdFromURI } from '../../utils/resources/ids';
import {
    CreateCompanyPayload,
    HomologationMedia,
    LinkPrestatairePayload,
    PrestataireAffiliation,
    PrestataireListRow,
    PrestataireSiretLookup,
    UpdatePrestatairePayload,
    scopesFromAffiliation,
} from './types';

/** Prestataire affiliations live on coreapi-v2; the rest of the backoffice still uses V1. */
const entrypoint = () => process.env.REACT_APP_API_V2_ENTRYPOINT || '';

const authHeaders = (extra: Record<string, string> = {}): HeadersInit => {
    const token = getToken();
    return {
        Authorization: `Bearer ${token}`.trim(),
        accept: 'application/ld+json',
        ...extra,
    };
};

const parseErrorMessage = async (response: Response): Promise<string> => {
    try {
        const data = await response.json();
        if (typeof data?.['hydra:description'] === 'string') {
            return data['hydra:description'];
        }
        if (typeof data?.detail === 'string') {
            return data.detail;
        }
        if (typeof data?.message === 'string') {
            return data.message;
        }
        if (typeof data?.error === 'string') {
            return data.error;
        }
    } catch {
        // ignore
    }
    return `HTTP ${response.status}`;
};

async function ensureOk(response: Response): Promise<Response> {
    if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
    }
    return response;
}

export const normalizeHomologationMedia = (
    media: HomologationMedia | string | null | undefined,
): HomologationMedia | null => {
    if (!media) {
        return null;
    }
    if (typeof media === 'string') {
        const id = extractIdFromURI(media);
        return id ? { id, '@id': media } : null;
    }
    const id = media.id || extractIdFromURI(media['@id'] || '') || '';
    return id ? { ...media, id } : null;
};

const resolvePrestataireCompany = (
    prestataire: PrestataireAffiliation['prestataire'],
): {
    name: string;
    email: string;
    invoiceId: string;
    street: string;
    postalCode: string;
    city: string;
} => {
    if (!prestataire || typeof prestataire === 'string') {
        return { name: '', email: '', invoiceId: '', street: '', postalCode: '', city: '' };
    }
    return {
        name: prestataire.name || '',
        email: prestataire.email || '',
        invoiceId: prestataire.invoiceId || '',
        street: prestataire.street || '',
        postalCode: prestataire.postalCode || '',
        city: prestataire.city || '',
    };
};

/** One list row per affiliated prestataire company (not per user). */
export const flattenAffiliationsToRows = (affiliations: PrestataireAffiliation[]): PrestataireListRow[] => {
    return affiliations.map((affiliation) => {
        const company = resolvePrestataireCompany(affiliation.prestataire);
        const users = Array.isArray(affiliation.prestataireUsers) ? affiliation.prestataireUsers : [];
        const contactEmail = company.email || users.find((user) => Boolean(user.email))?.email || '';

        const scopes = scopesFromAffiliation(affiliation);
        const types = scopes.map((scope) => scope.type);
        const departmentCodes = Array.from(new Set(scopes.flatMap((scope) => scope.departmentCodes || [])));

        return {
            key: affiliation.id,
            affiliationId: affiliation.id,
            companyName: company.name,
            companyEmail: contactEmail || '',
            invoiceId: company.invoiceId,
            street: company.street,
            postalCode: company.postalCode,
            city: company.city,
            types,
            departmentCodes,
            scopes,
            homologationMedia: normalizeHomologationMedia(affiliation.homologationMedia),
        };
    });
};

export async function fetchPrestataireAffiliations(): Promise<PrestataireAffiliation[]> {
    const response = await ensureOk(
        await fetch(`${entrypoint()}/anc/prestataire_affiliations`, {
            method: 'GET',
            headers: authHeaders(),
        }),
    );
    const data = await response.json();
    if (Array.isArray(data)) {
        return data;
    }
    if (Array.isArray(data?.['hydra:member'])) {
        return data['hydra:member'];
    }
    return [];
}

/** SIRET is stored without spaces (invoiceId "anc-hub:<siret>"). */
export const normalizeSiret = (siret: string): string => siret.replace(/\s+/g, '');

export const isValidSiret = (siret: string): boolean => /^\d{14}$/.test(normalizeSiret(siret));

export async function lookupPrestataireBySiret(siret: string): Promise<PrestataireSiretLookup> {
    const query = new URLSearchParams({ siret: normalizeSiret(siret) });
    const response = await ensureOk(
        await fetch(`${entrypoint()}/anc/prestataire_companies/lookup?${query.toString()}`, {
            method: 'GET',
            headers: authHeaders({ accept: 'application/json' }),
        }),
    );
    return response.json();
}

const deduplicationId = () => `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 15)}`;

export async function linkPrestataireAffiliation(payload: LinkPrestatairePayload): Promise<PrestataireAffiliation> {
    const response = await ensureOk(
        await fetch(`${entrypoint()}/anc/prestataire_affiliations/link`, {
            method: 'POST',
            headers: authHeaders({ 'content-type': 'application/json', 'x-deduplication-id': deduplicationId() }),
            body: JSON.stringify(payload),
        }),
    );
    return response.json();
}

/** Generic company creation; with `provider`, the backend also links it to the current fabricant (one transaction). */
export async function createCompany(payload: CreateCompanyPayload): Promise<unknown> {
    const response = await ensureOk(
        await fetch(`${entrypoint()}/anc/companies`, {
            method: 'POST',
            headers: authHeaders({ 'content-type': 'application/json', 'x-deduplication-id': deduplicationId() }),
            body: JSON.stringify(payload),
        }),
    );
    return response.json();
}

export async function updatePrestataireAffiliation(
    id: string,
    payload: UpdatePrestatairePayload,
): Promise<PrestataireAffiliation> {
    const response = await ensureOk(
        await fetch(`${entrypoint()}/anc/prestataire_affiliations/${id}`, {
            method: 'PATCH',
            headers: authHeaders({
                'content-type': 'application/merge-patch+json',
                'x-deduplication-id': deduplicationId(),
            }),
            body: JSON.stringify(payload),
        }),
    );
    return response.json();
}

export async function deletePrestataireAffiliation(id: string): Promise<void> {
    await ensureOk(
        await fetch(`${entrypoint()}/anc/prestataire_affiliations/${id}`, {
            method: 'DELETE',
            headers: authHeaders(),
        }),
    );
}

export const composeHomologationPdfUrl = (media: HomologationMedia): string | null => {
    const mediaId = media.id || extractIdFromURI(media['@id'] || '');
    const token = media.contentUrlTokenAccess;
    if (!mediaId || !token) {
        return null;
    }
    return `${process.env.REACT_APP_FILE_SYSTEM_MAPPER_ENTRYPOINT}/smartConnectView/file_system_mappers/${mediaId}/media/download.json?bearer=${token}`;
};

export const parseDepartmentCodes = (raw: string): number[] => {
    return raw
        .split(/[,\s;]+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => Number.parseInt(part, 10))
        .filter((code) => Number.isInteger(code) && code >= 1 && code <= 999);
};
